// Interpreta YYYY-MM-DD como data local (evita bug de UTC-3 mostrando dia anterior)
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  return new Date(dateStr + 'T00:00:00');
};

// Exibe nome completo (default) ou primeiro nome (short=true para espaços limitados)
export const displayName = (name, short = false) => {
  if (!name) return '';
  if (short) return name.split(' ')[0];
  return name;
};

// Rating médio dos últimos 30 dias (retorna null se sem dados no período)
export const getLastMonthRating = (user) => {
  if (!user?.ratingHistory || user.ratingHistory.length === 0) return null;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const recent = user.ratingHistory.filter(h => parseLocalDate(h.date) >= cutoff);
  if (recent.length === 0) return null;
  return recent.reduce((sum, h) => sum + h.rating, 0) / recent.length;
};

// Média dos últimos N jogos do ratingHistory
export const getRecentRating = (user, gameCount = 4) => {
  if (!user?.ratingHistory || user.ratingHistory.length === 0) return null;
  const recent = [...user.ratingHistory].slice(-gameCount);
  return recent.reduce((sum, h) => sum + h.rating, 0) / recent.length;
};

export const getCommunityRating = (user) => {
  if (!user) return 5;
  return user.communityRating !== null && user.communityRating !== undefined
    ? user.communityRating
    : user.manualRating || 5;
};

// Detecta notas discrepantes em um array de votos (escala 1-10).
// Requer mínimo 4 votos; remove votos com desvio > 2.5 pontos da média.
// Se a remoção deixar menos de 2 votos, retorna todos como válidos.
export const detectOutlierVotes = (votes) => {
  if (!votes || votes.length < 4) return { validVotes: votes || [], outlierVotes: [] };
  const mean = votes.reduce((a, b) => a + b, 0) / votes.length;
  const valid = votes.filter(v => Math.abs(v - mean) <= 2.5);
  const outliers = votes.filter(v => Math.abs(v - mean) > 2.5);
  if (valid.length < 2) return { validVotes: votes, outlierVotes: [] };
  return { validVotes: valid, outlierVotes: outliers };
};

// Distribuição bruta sem detectar outliers — usar em visualizações de grupo/jogo
// onde não se sabe se o rating armazenado foi calculado com ou sem outlier filter.
export const getRawVoteBreakdown = (game, playerId) => {
  if (!game?.votes) return null;
  const rawVotes = Object.values(game.votes)
    .map(sheet => sheet[playerId])
    .filter(v => v !== undefined && v !== null);
  if (rawVotes.length === 0) return null;
  const distribution = {};
  rawVotes.forEach(v => { const k = String(v); distribution[k] = (distribution[k] || 0) + 1; });
  const avg = rawVotes.reduce((a, b) => a + b, 0) / rawVotes.length;
  return { distribution, outlierVotes: [], filteredAvg: Math.round(avg * 10) / 10, totalVotes: rawVotes.length };
};

// Retorna distribuição de votos agrupada por nota para um jogador em um jogo.
// distribution: { "7": 4, "6": 3 } — contagem por nota (apenas votos válidos)
// outlierVotes: [10, 2] — notas descartadas (não entram na média)
export const getVoteBreakdown = (game, playerId) => {
  if (!game?.votes) return null;
  const rawVotes = Object.values(game.votes)
    .map(sheet => sheet[playerId])
    .filter(v => v !== undefined && v !== null);
  if (rawVotes.length === 0) return null;
  const { validVotes, outlierVotes } = detectOutlierVotes(rawVotes);
  const filteredAvg = validVotes.length > 0
    ? Math.round((validVotes.reduce((a, b) => a + b, 0) / validVotes.length) * 10) / 10
    : 0;
  const distribution = {};
  validVotes.forEach(v => {
    const key = String(v);
    distribution[key] = (distribution[key] || 0) + 1;
  });
  return { distribution, outlierVotes, filteredAvg, totalVotes: rawVotes.length };
};

// users pode ser um objeto { uid: profile } ou um array
export const calculateGameStats = (game, users) => {
  const getUser = (pid) => {
    if (Array.isArray(users)) return users.find(u => u.id === pid);
    return users[pid] || null;
  };

  const playerStats = game.playerStats || {};

  // ─── Sorted Players (votos) ───
  let results = [];
  let mvp = null;
  let pereba = null;

  if (game.votes) {
    const votesByPlayer = {};
    Object.values(game.votes).forEach(voteSheet => {
      Object.entries(voteSheet).forEach(([pid, val]) => {
        if (!votesByPlayer[pid]) votesByPlayer[pid] = [];
        votesByPlayer[pid].push(val);
      });
    });

    const useFilter = !!game.outlierFilterApplied;

    results = Object.entries(votesByPlayer).map(([pid, votes]) => {
      const validVotes = useFilter ? detectOutlierVotes(votes).validVotes : votes;
      const avg = validVotes.reduce((a, b) => a + b, 0) / validVotes.length;
      const ps = playerStats[pid] || {};
      return {
        id: pid,
        avg,
        votesCount: votes.length,
        goals: ps.goals || 0,
        assists: ps.assists || 0,
        user: getUser(pid),
      };
    }).sort((a, b) =>
      b.avg - a.avg ||
      b.votesCount - a.votesCount ||
      b.goals - a.goals ||
      b.assists - a.assists ||
      (a.user?.name || '').localeCompare(b.user?.name || '')
    );

    mvp = results[0] || null;
    pereba = results[results.length - 1] || null;
  }

  // ─── Artilheiro / Garçom (usa ranking como critério de desempate) ───
  const rankIndex = (pid) => {
    const idx = results.findIndex(r => r.id === pid);
    return idx >= 0 ? idx : results.length;
  };

  let topScorer = null;
  let topAssister = null;
  Object.entries(playerStats).forEach(([pid, st]) => {
    const goals = st?.goals || 0;
    const assists = st?.assists || 0;

    if (goals > 0 && (!topScorer || goals > topScorer.goals)) {
      topScorer = { id: pid, goals, assists, user: getUser(pid) };
    } else if (topScorer && goals === topScorer.goals && goals > 0) {
      if (rankIndex(pid) < rankIndex(topScorer.id) ||
         (rankIndex(pid) === rankIndex(topScorer.id) && assists > topScorer.assists)) {
        topScorer = { id: pid, goals, assists, user: getUser(pid) };
      }
    }

    if (assists > 0 && (!topAssister || assists > topAssister.assists)) {
      topAssister = { id: pid, assists, goals, user: getUser(pid) };
    } else if (topAssister && assists === topAssister.assists && assists > 0) {
      if (rankIndex(pid) < rankIndex(topAssister.id) ||
         (rankIndex(pid) === rankIndex(topAssister.id) && goals > topAssister.goals)) {
        topAssister = { id: pid, assists, goals, user: getUser(pid) };
      }
    }
  });

  return {
    sortedPlayers: results,
    mvp,
    pereba,
    topScorer,
    topAssister,
  };
};

/**
 * Distribui um novo jogador em times já sorteados, colocando no time com menos jogadores
 * (ou em teamA em caso de empate). Retorna { teamA, teamB } atualizados, ou null se o
 * jogo ainda não tem times sorteados — sinalizando que nenhum update em `teams` é necessário.
 *
 * Usado para integrar check-ins pós-sorteio (membros, externos aprovados, avulsos) à
 * escalação sem precisar refazer o sorteio.
 */
export const distributeNewPlayer = (game, playerObj) => {
  if (!game?.teams || !Array.isArray(game.teams.teamA) || !Array.isArray(game.teams.teamB)) return null;
  // Evitar duplicação: se já está escalado, não mexer
  const alreadyInA = game.teams.teamA.some(p => p.id === playerObj.id);
  const alreadyInB = game.teams.teamB.some(p => p.id === playerObj.id);
  if (alreadyInA || alreadyInB) return null;
  const a = [...game.teams.teamA];
  const b = [...game.teams.teamB];
  if (a.length <= b.length) a.push(playerObj); else b.push(playerObj);
  return { teamA: a, teamB: b };
};